import { Spinner } from '@ibsys/kit';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import type { DownloadTemplateSelection } from '@/entities/download-template';
import type { ConversationPipeline, ExpertReportPipelineScope, TranscriptArchiveProgress } from '@/entities/search';
import { ConfirmationButton } from '@/shared/ui';
import { PipelineActions } from './PipelineActions';
import { TranscriptArchiveAction } from './TranscriptArchiveAction';

type Props = {
  /** Число отмеченных диалогов — выводится в блоке «Выбрано: N» и называется в подтверждении удаления */
  selectedCount: number;
  /** Есть над чем выполнять действие: отмечена хотя бы одна строка */
  hasSelection: boolean;
  /** Идёт сбор состава «выбрать всё» — вместо числа показывается индикатор ожидания */
  isPreparingSelection: boolean;
  /** Запуск обработки доступен пользователю: право на массовый запуск */
  canRunPipeline: boolean;
  /** Запуск обработки сейчас не запускается: он уже идёт, идёт удаление или набирается «выбрать всё» */
  isRunPipelineLocked: boolean;
  /** Запуск выбранной обработки, у генерации заключений — с выбранной областью */
  onRunPipeline: (pipeline: ConversationPipeline, scope?: ExpertReportPipelineScope) => void;
  /** Удаление доступно пользователю: право на удаление диалогов */
  canDeleteConversations: boolean;
  /** Удаление сейчас не запускается: идёт любое действие над отмеченными или набирается «выбрать всё» */
  isDeleteLocked: boolean;
  /** Идёт удаление — подтверждение показывает ожидание */
  isDeletePending: boolean;
  /** Удаление отмеченных диалогов после подтверждения */
  onDeleteConversations: () => Promise<void>;
  /** Скачивание транскрипций архивом доступно пользователю */
  canDownloadTranscriptArchive: boolean;
  /** Архив не запускается из-за другого действия: идёт удаление или набирается «выбрать всё» */
  isTranscriptArchiveLocked: boolean;
  /** Архив транскрипций формируется — повторный запуск заблокирован */
  isTranscriptArchivePending: boolean;
  /** Ход формирования архива транскрипций; `null`, пока формирование не запущено */
  transcriptArchiveProgress: TranscriptArchiveProgress | null;
  /** Запуск архива транскрипций с выбранными шаблоном и форматом */
  onDownloadTranscriptArchive: (selection: DownloadTemplateSelection) => void;
};

/**
 * Блок массовых действий, раскрываемый кнопкой «Действия» под строкой поиска (по аналогии с
 * панелью поиска по содержимому диалога): действия над отмеченными диалогами — каждое под своим
 * разрешением — и счётчик отмеченных. Сам выбор ведётся чекбоксами в строках и мастер-чекбоксом
 * в шапке таблицы.
 *
 * Ряд переносится сам, только когда действиям не хватает ширины строки: порог по ширине окна не
 * годится — строке тесно и на широком окне с открытой карточкой диалога, а без переноса крайние
 * кнопки и счётчик уходят за край экрана.
 */
export const MassActionsBar: FC<Props> = ({
  selectedCount,
  hasSelection,
  isPreparingSelection,
  canRunPipeline,
  isRunPipelineLocked,
  onRunPipeline,
  canDeleteConversations,
  isDeleteLocked,
  isDeletePending,
  onDeleteConversations,
  canDownloadTranscriptArchive,
  isTranscriptArchiveLocked,
  isTranscriptArchivePending,
  transcriptArchiveProgress,
  onDownloadTranscriptArchive,
}) => {
  const { t } = useTranslation(['dialogs']);

  return (
    <div className="flex flex-row flex-wrap items-center gap-x-3 gap-y-2">
      {canRunPipeline && (
        <PipelineActions hasSelection={hasSelection} isPending={isRunPipelineLocked} onRun={onRunPipeline} />
      )}
      {canDownloadTranscriptArchive && (
        <TranscriptArchiveAction
          hasSelection={hasSelection}
          isLocked={isTranscriptArchiveLocked}
          isPending={isTranscriptArchivePending}
          progress={transcriptArchiveProgress}
          onDownload={onDownloadTranscriptArchive}
        />
      )}
      {canDeleteConversations && (
        <ConfirmationButton
          onConfirm={onDeleteConversations}
          isLoadingConfirm={isDeletePending}
          title={t('dialogs:dialogListSection.deleteDialogs.confirmTitle')}
          description={t('dialogs:dialogListSection.deleteDialogs.confirmDescription', { count: selectedCount })}
          trigger={{
            variant: 'secondary',
            size: 'sm',
            disabled: !hasSelection || isDeleteLocked,
            children: t('dialogs:dialogListSection.deleteDialogs.label'),
          }}
        />
      )}
      {isPreparingSelection ? (
        // «Выбрать всё» ещё собирает состав выборки — вместо промежуточного числа показываем ожидание.
        <span className="flex items-center gap-2 text-sm whitespace-nowrap text-muted-foreground">
          {t('dialogs:dialogListSection.massActions.selectedPreparing')}
          <Spinner className="size-4" />
        </span>
      ) : (
        <span className="text-sm whitespace-nowrap text-muted-foreground">
          {t('dialogs:dialogListSection.massActions.selectedCount', { count: selectedCount })}
        </span>
      )}
    </div>
  );
};
